import React, { useEffect, useState } from 'react';
import {
  Button,
  Card,
  Col,
  Container,
  Spinner,
  Badge,
  Stack,
  Toast,
  ToastContainer,
} from 'react-bootstrap';
import type { SupplierCompanyProfile, UserOrganisation } from '../types/types';
import { useSelector } from 'react-redux';
import { supabase } from '../lib/supabase';
import SupplierProfileBuilder from '../components/supplier/SupplierProfileBuilder';
import SupplierComplianceProfile from '../assets/SupplierComplianceProfile.jpg';
import SupplierComplianceProfileExists from '../assets/SupplierComplianceProfile_exists.jpg';
import { Link } from 'react-router-dom';
import { gToastPosition, gToastSentiment } from '../util/constst';
import { tracking } from '../util/tracking';

const MyProfile: React.FC = () => {
  const [isLoading, setIsLoading] = useState(false);
  const [profile, setProfile] = useState<SupplierCompanyProfile | null>(null);
  const [creatingProfile, setCreatingProfile] = useState(false);
  const [publishing, setPublishing] = useState(false);
  const [toastMessage, setToastMessage] = useState('');
  const [showToast, setShowToast] = useState(false);

  const selectedOrganisation = useSelector(
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    (state: any) => state.organisation.selected,
  ) as UserOrganisation | undefined;

  const supplierId = selectedOrganisation?.organisation?.id;

  useEffect(() => {
    if (!supplierId) return;

    const fetchProfile = async () => {
      setIsLoading(true);

      const { data, error } = await supabase
        .from('supplier_company_profiles')
        .select('*')
        .eq('supplier_id', supplierId)
        .maybeSingle();

      if (error && error.code !== 'PGRST116') {
        console.error('Error fetching supplier profile:', error);
      }

      if (data) {
        setProfile(data);
      }

      setIsLoading(false);
    };

    fetchProfile();
  }, [supplierId]);

  const handlePublishToggle = async () => {
    if (!profile) return;

    setPublishing(true);

    const { data, error } = await supabase
      .from('supplier_company_profiles')
      .update({
        profile_status:
          profile.profile_status === 'published' ? 'draft' : 'published',
      })
      .eq('id', profile.id)
      .select()
      .single();

    if (error) {
      console.error('Error updating publish status:', error);
    } else {
      setProfile(data);
    }

    setPublishing(false);

    tracking.log('UI', 'profile_publish_toggle', {
      supplierId,
      newStatus: data?.profile_status,
    });
  };

  const handlePreview = () => {
    if (!profile) return;
    window.open(`/supplier-profile/${profile.id}/preview`, '_blank');
  };

  const renderStatus = () => {
    if (!profile) return null;

    if (profile.profile_status === 'published') {
      return <Badge bg="success">Published</Badge>;
    }

    return <Badge bg="secondary">Draft</Badge>;
  };

  const content = (profile?: SupplierCompanyProfile) => {
    const isPublished = profile?.profile_status === 'published';

    if (isLoading) {
      return (
        <div className="text-center my-5">
          <Spinner animation="border" role="status" />
          <p className="mt-3">Loading your profile...</p>
        </div>
      );
    }

    if (profile) {
      return (
        <Col md={5}>
          <Card className="mb-4 shadow-sm">
            <p style={{ position: 'absolute', top: 10, right: 10 }}>
              {renderStatus()}
            </p>

            <Card.Img
              variant="top"
              src={SupplierComplianceProfileExists}
              alt="Create Your Compliance Profile"
              className="mb-3"
            />
            <Card.Body className="pt-1">
              <Stack direction="horizontal" gap={2}>
                <Button
                  variant="outline-primary"
                  size="sm"
                  onClick={handlePreview}
                  disabled={!profile}
                >
                  Preview
                </Button>

                <Button
                  variant={isPublished ? 'outline-danger' : 'success'}
                  size="sm"
                  onClick={handlePublishToggle}
                  disabled={publishing}
                >
                  {isPublished ? 'Unpublish' : 'Publish'}
                </Button>

                <Link to="/my-profile/builder">
                  <Button variant="secondary" size="sm">
                    Edit
                  </Button>
                </Link>
                {isPublished && (
                  <Button
                    variant="secondary"
                    size="sm"
                    onClick={() => {
                      const url = `${window.location.origin}/supplier/${profile.id}`;
                      navigator.clipboard.writeText(url);
                      setToastMessage('Profile link copied to clipboard!');
                      setShowToast(true);
                      tracking.log('UI', 'profile_copy_link', { supplierId });
                    }}
                  >
                    Copy link
                  </Button>
                )}
              </Stack>
              <Card.Title className="mt-4">
                {profile.company_name || 'Not set'}
              </Card.Title>
              <p>
                <strong>Updated:</strong>{' '}
                {profile.updated_at
                  ? new Date(profile.updated_at).toLocaleDateString()
                  : 'N/A'}
              </p>
            </Card.Body>
          </Card>
        </Col>
      );
    } else {
      return (
        <Col md={5}>
          <Card className="mb-4 shadow-sm">
            <Card.Img
              variant="top"
              src={SupplierComplianceProfile}
              alt="Create Your Compliance Profile"
              className="mb-3"
            />
            <Card.Body>
              <h5>Create Your Compliance Profile</h5>
              <p>
                You haven't created your compliance profile yet. A public
                profile helps buyers understand your compliance efforts and
                policies before engaging with you.
              </p>

              <Link to="/my-profile/builder">
                <Button variant="primary" size="sm">
                  Create profile
                </Button>
              </Link>
            </Card.Body>
          </Card>
        </Col>
      );
    }
  };

  return (
    <Container fluid>
      <h3 className="mb-4">Your Public Compliance Profile</h3>

      <Col md={8} className="mb-4">
        <p>
          Build your public profile to showcase your compliance efforts to
          buyers. You can preview how your profile looks before publishing it
          live. Once published, you can share the profile link with buyers to
          demonstrate your commitment to responsible sourcing.
        </p>
      </Col>

      {content(profile || undefined)}

      <SupplierProfileBuilder
        show={creatingProfile}
        onHide={() => setCreatingProfile(false)}
        supplierId={supplierId || ''}
        organisation={selectedOrganisation?.organisation}
      />
      <ToastContainer position={gToastPosition} className="p-3">
        <Toast
          bg={gToastSentiment}
          onClose={() => setShowToast(false)}
          show={showToast}
          delay={3000}
          autohide
        >
          <Toast.Body>{toastMessage}</Toast.Body>
        </Toast>
      </ToastContainer>
    </Container>
  );
};

export default MyProfile;
